// A tab's pages (docs/specs/NAVIGATION_2026_10.md), drawn by the layout beneath the current tab on the spine when the
// tab has more than one page. Every page shows in the registry's order, so a page never moves when it is opened or something waits on it
// (Craig, 19 Oct 2026). Advanced pages show only with the tab's Advanced switch on, remembered per tab in this browser;
// opening one by its address shows them. A page whose set-up part is switched off (`part`, read from crm_setup_state)
// stays in its place, marked "Off"; every address still opens.
import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MODULE_DEFS } from "../../modules";
import { pagePath, type ModulePage } from "../../modules/types";
import { useWorkspace } from "../../lib/session";
import { AttentionContext } from "../../lib/attention";
import type { SetupView } from "../../lib/setupFlow";
import "./step-pages.css";

export interface StepPageItem {
  path: string;
  label: string;
  advanced?: boolean;
}

/** "off" when set-up recorded the part as switched off, "on" when on, "unknown" when it can't be told. */
export type PartState = "on" | "off" | "unknown";

/** The parts set-up recorded, as "<module>.<part>" → on or off, or null when they can't be relied on. */
export function partsFromSetup(v: SetupView | null | undefined): Record<string, boolean> | null {
  // A business from before guided set-up shows every module on, and its parts are only a guess from its settings.
  if (!v || v.legacy) return null;
  const out: Record<string, boolean> = {};
  for (const [m, mv] of Object.entries(v.modules ?? {})) {
    // A module set-up never switched on says nothing about its parts.
    if (!mv || mv.state !== "on") continue;
    for (const [p, pv] of Object.entries(mv.parts ?? {})) out[`${m}.${p}`] = pv.on === true;
  }
  return out;
}

export function partState(parts: Record<string, boolean> | null, part: string | undefined): PartState {
  if (!part) return "on";
  if (!parts || !(part in parts)) return "unknown";
  return parts[part] ? "on" : "off";
}

/** Whether each page of a step is switched off in set-up (shown in place, marked "Off"); the list never reorders. */
export function offPages(pages: Pick<ModulePage, "part">[], parts: Record<string, boolean> | null): boolean[] {
  return pages.map((p) => partState(parts, p.part) === "off");
}

// One read of the set-up per business while the app is open: the side list renders on every module page.
const partsCache = new Map<string, Promise<Record<string, boolean> | null>>();

/** Forget the cached parts (set-up changed them, or a test starts again). */
export function forgetParts(): void {
  partsCache.clear();
}

/** The set-up parts recorded for this business (read once per business), or null when they can't be relied on. */
export function useParts(wanted: boolean): Record<string, boolean> | null | undefined {
  const ws = useWorkspace();
  const crm = ws.crm;
  const key = ws.tenant?.id ?? "";
  const [parts, setParts] = useState<Record<string, boolean> | null | undefined>(undefined);
  useEffect(() => {
    if (!wanted || !crm || !key) return;
    let live = true;
    let p = partsCache.get(key);
    if (!p) {
      p = crm.setupState().then(partsFromSetup, () => null);
      partsCache.set(key, p);
    }
    void p.then((v) => live && setParts(v));
    return () => {
      live = false;
    };
  }, [wanted, crm, key]);
  return wanted ? parts : null;
}

function PageLink({ item, off, active, waiting }: { item: StepPageItem; off: boolean; active: boolean; waiting: number }) {
  const name = off ? `${item.label}, switched off` : waiting ? `${item.label}, ${waiting} waiting` : item.label;
  return (
    <li>
      <Link
        to={item.path}
        className={`step-page${active ? " is-active" : ""}${off ? " is-off" : ""}`}
        aria-current={active ? "page" : undefined}
        aria-label={name !== item.label ? name : undefined}
      >
        <span>{item.label}</span>
        {waiting > 0 && !off ? (
          <span className="count" aria-hidden="true">
            {waiting}
          </span>
        ) : null}
        {off ? (
          <span className="step-off" aria-hidden="true">
            Off
          </span>
        ) : null}
      </Link>
    </li>
  );
}

const ADVANCED_KEY = (tab: string) => `ogma.advanced.${tab}`;

function readAdvanced(tab: string): boolean {
  try {
    return localStorage.getItem(ADVANCED_KEY(tab)) === "1";
  } catch {
    return false;
  }
}

function saveAdvanced(tab: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(ADVANCED_KEY(tab), "1");
    else localStorage.removeItem(ADVANCED_KEY(tab));
  } catch {
    // Private windows and blocked storage: the switch still works until the page is left.
  }
}

/**
 * The pages of a tab, as a short list beneath the tab on the spine.
 * `current` is the page open now (a detail page counts as its list). `module` and `step` find each page's registry
 * entry (its `part`); without them no page is marked off.
 */
export function StepPages({ label, items, current, module, step }: { label: string; items: StepPageItem[]; current: string; module?: string; step?: string }) {
  const tabPath = step && module ? `/${module}/${step}` : label;
  // What waits on each page, beside its name (lib/attention.ts).
  const attention = useContext(AttentionContext);
  const [advanced, setAdvanced] = useState(() => readAdvanced(tabPath));
  useEffect(() => setAdvanced(readAdvanced(tabPath)), [tabPath]);
  const def = MODULE_DEFS.find((m) => m.key === module);
  const pages = useMemo((): Pick<ModulePage, "part">[] => {
    const tab = def?.layout === "steps" ? def.tabs.find((t) => t.key === step) : undefined;
    if (!def || !tab) return items.map(() => ({}));
    return items.map((i) => tab.pages.find((p) => pagePath(def.key, tab.key, p.slug) === i.path) ?? {});
  }, [def, step, items]);
  const parts = useParts(pages.some((p) => !!p.part));
  const off = offPages(pages, parts ?? null);
  const hasAdvanced = items.some((i) => i.advanced);
  const showAll = advanced || items.some((i) => i.advanced && i.path === current);
  const toggle = () => {
    saveAdvanced(tabPath, !advanced);
    setAdvanced(!advanced);
  };
  return (
    <nav className="step-pages" aria-label={label}>
      <ul>
        {items.map((i, n) =>
          !i.advanced || showAll ? <PageLink key={i.path} item={i} off={off[n]!} active={i.path === current} waiting={attention?.pages[i.path] ?? 0} /> : null,
        )}
      </ul>
      {hasAdvanced && (
        <label className="step-advanced small">
          <input type="checkbox" checked={showAll} disabled={showAll && !advanced} onChange={toggle} />
          Advanced
        </label>
      )}
    </nav>
  );
}
